import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { SelectBox } from 'devextreme-react';
import { NuvoTooltip } from '@nuvolo/nuux-core/components/NuvoTooltip';
import { useNavigate } from 'react-router-dom';
import DataSource from 'devextreme/data/data_source';
import { buildFilters } from '#/common/utils/buildFilters';
import type { ISelectBoxOptions } from 'devextreme-react/select-box';
import useDataStore, { DataStoreOptions } from '#/common/hooks/useDataStore';
import parseUrl from '#/common/utils/parsers/parseUrl';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import type { EditorProps } from './EditorProps';
import { border, borderRadius } from './common';
import If from '../../If';
import Validator, { RequiredRule } from 'devextreme-react/validator';
import { createScrollCloseDropdownOptions } from '../utils/dropdownScrollClose';
import { getItemOptions } from '../formItemOption';
import TMMButton from '../../TMMButton';
import SelectCard from '#/common/components/SelectCard';
import dotWalk from '#/common/utils/dotWalk';
import useSession from '#/common/hooks/useSession';
import useToast from '#/common/hooks/useToast';
import { UserRole } from '#/common/types/userTypes';
import RelatedItemsCreateModal from './RelatedItemsCreateModal';
import type { RelatedItemsCreateModalConfig } from './RelatedItemsCreateModal';
import type { ValueChangedEvent } from 'devextreme/ui/select_box';

type RelatedItemsCreateActionResult = {
  selectedValue?: unknown;
  keepOpen?: boolean;
};

export type RelatedItemsCreateServices = {
  createAssetType: (...args: any[]) => Promise<any>;
  createManufacturer: (...args: any[]) => Promise<any>;
  createModel: (...args: any[]) => Promise<any>;
};

interface RelatedItemsOptions extends ISelectBoxOptions {
  valueExpr?: string;
  validationRules?: any[];
  button?: {
    hide?: boolean;
    tooltip?: string;
    url?: string;
    icon?: string;
    desktopOnly?: boolean;
    readOnly?: boolean;
    enabledForRoles?: UserRole[];
  };
  skipTenantFilter?: boolean;
  dataStoreOptions?: Pick<
    DataStoreOptions<unknown>,
    'getItemList' | 'getItem' | 'filter' | 'accessTo'
  >;
  subtitleExpr?: string | ((data: Record<string, unknown>) => string);
  customItemRender?: (
    data: Record<string, unknown>,
    valueExpr: string,
    displayExpr?: string | ((item: any) => string),
  ) => React.ReactNode;
  dropdownClassName?: string;
  dynamic?: {
    watch: string[];
    build: (ctx: { record: Record<string, unknown>; value: unknown }) => any[] | null;
    onWatchChange?: (args: {
      changed: { field: string; value: any; prev?: any }[];
      record: Record<string, any>;
      value: any;
      form: any;
    }) => void;
  };
  createItemAction?: {
    label: string;
    onCreate?: RelatedItemsCreateServices;
    getInitialValues?: (ctx: { formData: Record<string, unknown> }) => Record<string, unknown>;
    onPrimaryAction?: (ctx: {
      values: Record<string, unknown>;
      formData: Record<string, unknown>;
      setFieldValue: (value: unknown) => void;
      alert: (status: 'success' | 'error' | 'warning', message: string) => Promise<void>;
      onCreate: RelatedItemsCreateServices;
    }) => Promise<RelatedItemsCreateActionResult | void> | RelatedItemsCreateActionResult | void;
    modal: RelatedItemsCreateModalConfig;
  };
}

export interface RelatedItemsProps {
  editorType: 'RelatedItems';
  editorOptions: RelatedItemsOptions;
}

export type { RelatedItemsOptions };

const defaultTenantFilter = (record: Record<string, unknown>) => {
  const tenantId = record?.tenantId ?? null;
  return tenantId ? ['tenantId', '=', tenantId] : null;
};

const RelatedItems: React.FC<EditorProps<RelatedItemsOptions>> = (props) => {
  const { dataField, isRequired, editorOptions, form } = props;
  editorOptions.readOnly = form.option('readOnly') || editorOptions.readOnly;

  const {
    button,
    dataSource,
    displayExpr,
    valueExpr = 'id',
    dataStoreOptions,
    skipTenantFilter = false,
    searchEnabled = true,
    dynamic,
    subtitleExpr,
    customItemRender,
    dropdownClassName,
    createItemAction,
    value: initValue,
    ...selectProps
  } = editorOptions;

  const isItemRequired = isRequired || getItemOptions(form, dataField).isRequired || undefined;
  const validationGroup = form.option('validationGroup') || 'FormValidationGroup';

  const { user } = useSession();
  const { alert } = useToast();
  const currentRole = user?.role as UserRole | undefined;

  const {
    hide = false,
    tooltip = '',
    url = '',
    icon = 'arrow-right',
    desktopOnly = false,
    readOnly: isBtnReadOnly = false,
    enabledForRoles,
  } = button ?? {};

  const isDisabledForCurrentRole =
    !!enabledForRoles && (!currentRole || !enabledForRoles.includes(currentRole));

  const [watchTick, setWatchTick] = useState(0); // re-render trigger for parent changes
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createModalInitialValues, setCreateModalInitialValues] = useState<
    Record<string, unknown> | undefined
  >(undefined);
  const record = (form.option('formData') as Record<string, unknown>) || {};
  const selectedValue = record[dataField];
  const [currentValue, setCurrentValue] = useState<unknown>(selectedValue ?? initValue ?? null);
  const tooltipMsg = parseUrl(tooltip, { value: currentValue });

  const navigate = useNavigate();

  // Listen for field changes → notify + bump tick
  useEffect(() => {
    if (!dynamic?.watch?.length || !form) return;

    const handler = (e: { dataField?: string; value?: any; previousValue?: any }) => {
      const field = e?.dataField;
      if (!field || !dynamic.watch.includes(field)) return;

      dynamic.onWatchChange?.({
        changed: [{ field, value: e.value, prev: e.previousValue }],
        record: form.option('formData') as Record<string, any>,
        value: currentValue,
        form,
      });

      setWatchTick((t) => t + 1); // force recompute of record/filters
    };

    (form as any).on?.('fieldDataChanged', handler);
    return () => (form as any).off?.('fieldDataChanged', handler);
  }, [dynamic?.watch, dynamic?.onWatchChange, form, currentValue]);

  // Keep this editor controlled and synced with external formData changes.
  useEffect(() => {
    if (!form) return;

    const syncFromForm = () => {
      const formData = (form.option('formData') as Record<string, unknown>) || {};
      // don't fall back to stale initValue once the field has been explicitly set (e.g. cleared)
      const hasOwnValue = Object.hasOwn(formData, dataField);
      const next = hasOwnValue ? (formData[dataField] ?? null) : (initValue ?? null);
      setCurrentValue(next);
    };

    syncFromForm();

    const handler = (e: { dataField?: string }) => {
      if (e?.dataField !== dataField) return;
      syncFromForm();
    };

    (form as any).on?.('fieldDataChanged', handler);
    return () => (form as any).off?.('fieldDataChanged', handler);
  }, [form, dataField, initValue]);

  // Build filters: tenant + base + dynamic
  const tenantFilter = useMemo(
    () => (skipTenantFilter ? null : defaultTenantFilter(record)),
    [skipTenantFilter, record],
  );
  const baseFilter = dataStoreOptions?.filter ?? null;

  const buttonDisabled = !currentValue || isBtnReadOnly || isDisabledForCurrentRole;
  const tooltipEnabled = !!tooltip && !buttonDisabled;

  const dynamicFilter = useMemo(() => {
    if (!dynamic?.build) return null;
    try {
      return dynamic.build({ record: record as any, value: currentValue });
    } catch {
      return null;
    }
  }, [dynamic?.build, record, currentValue, watchTick]);

  const mergedFilter = useMemo(() => {
    const parts = [baseFilter, tenantFilter, dynamicFilter].filter(
      (part): part is any[] => Array.isArray(part) && part.length > 0,
    );
    if (!parts.length) return undefined;
    if (parts.length === 1) return parts[0];

    return buildFilters(parts as unknown[][]) ?? [];
  }, [baseFilter, tenantFilter, dynamicFilter]);

  // A key that changes when parents or mergedFilter change
  const dynamicKey = useMemo(() => {
    const watchVals = (dynamic?.watch ?? []).map((f) => (record as any)?.[f] ?? null);
    return JSON.stringify({ watchVals, mergedFilter });
  }, [dynamic?.watch, record, mergedFilter]);

  const queryDataStore = useDataStore<Record<string, unknown>>({
    ...dataStoreOptions,
    filter: mergedFilter,
    cacheEnabled: true,
    key: valueExpr,
  });

  const ds = dataSource || queryDataStore;
  const selectDataSource = useMemo(
    () =>
      new DataSource({
        store: ds as any,
        paginate: true,
        pageSize: 10,
      }),
    [ds],
  );

  const onClick = useCallback(() => {
    const urlWithValue = parseUrl(url, { value: currentValue });
    navigate(urlWithValue);
  }, [navigate, currentValue, url]);

  const onValueChanged = useCallback(
    (e: ValueChangedEvent) => {
      const newValue = e.value ?? null;
      setCurrentValue(newValue);
      form.updateData({
        [dataField]: newValue,
      });
    },
    [dataField, form],
  );

  const { isDesktop } = useViewport();
  const showButton = !hide && (!desktopOnly || isDesktop);

  const onCreateItemClick = useCallback(() => {
    const formData = (form.option('formData') as Record<string, unknown>) || {};
    const initialValues = createItemAction?.getInitialValues?.({ formData });
    setCreateModalInitialValues(initialValues);
    setShowCreateItemModal(true);
  }, [form, createItemAction, dataField]);

  const closeCreateItemModal = useCallback(() => {
    setShowCreateItemModal(false);
    setCreateModalInitialValues(undefined);
  }, [dataField]);

  const resolvedModal = useMemo(() => {
    const baseModal = createItemAction?.modal;
    if (!baseModal) {
      return { title: 'Create New Item' };
    }

    if (createItemAction?.onPrimaryAction && !baseModal.onPrimaryAction) {
      return {
        ...baseModal,
        initialValues: createModalInitialValues ?? baseModal.initialValues,
        onPrimaryAction: async ({ values, close }) => {
          if (!createItemAction.onCreate) {
            await alert('error', 'Create action is not configured.');
            return;
          }

          const currentFormData = (form.option('formData') as Record<string, unknown>) || {};
          const result = await createItemAction.onPrimaryAction?.({
            values,
            formData: currentFormData,
            setFieldValue: (value) => {
              form.updateData({
                [dataField]: value,
              });
            },
            alert,
            onCreate: createItemAction.onCreate,
          });

          if (result?.selectedValue !== undefined) {
            form.updateData({
              [dataField]: result.selectedValue,
            });
          }

          if (!result?.keepOpen) {
            close();
          }
        },
      } satisfies RelatedItemsCreateModalConfig;
    }

    return baseModal;
  }, [createItemAction, form, dataField, alert, createModalInitialValues]);

  // Custom item render using SelectCard
  const itemRender = useCallback(
    (data: Record<string, unknown>) => {
      if (!data) return null;

      // Use custom renderer if provided, otherwise use default
      if (customItemRender) {
        return customItemRender(data, valueExpr, displayExpr);
      }

      const id = String(data[valueExpr] || '');
      const title = String(
        displayExpr
          ? typeof displayExpr === 'string'
            ? dotWalk(data, displayExpr) || 'Unnamed'
            : displayExpr(data) || 'Unnamed'
          : 'Unnamed',
      );
      let subtitle: string | undefined;
      if (subtitleExpr) {
        if (typeof subtitleExpr === 'function') {
          subtitle = subtitleExpr(data);
        } else {
          const walked = dotWalk(data, subtitleExpr);
          subtitle = typeof walked === 'string' ? walked : '';
        }
      }

      return <SelectCard id={id} title={title} subtitle={subtitle} />;
    },
    [valueExpr, displayExpr, subtitleExpr, customItemRender],
  );

  // Dropdown options with card styling
  const dropdownOptions = useMemo(() => {
    const baseOptions = createScrollCloseDropdownOptions();
    const columnClass = dropdownClassName || 'cards-single-column';
    const toolbarItems = createItemAction
      ? [
          {
            toolbar: 'bottom' as const,
            location: 'after' as const,
            widget: 'dxButton' as const,
            options: {
              text: createItemAction.label,
              stylingMode: 'text',
              type: 'normal',
              width: '100%',
              elementAttr: {
                class: 'related-items-create-option-btn',
              },
              onClick: onCreateItemClick,
            },
          },
        ]
      : undefined;

    return {
      ...baseOptions,
      minHeight: 300,
      maxHeight: 300,
      toolbarItems,
      wrapperAttr: {
        ...baseOptions.wrapperAttr,
        class:
          `${baseOptions.wrapperAttr?.class || ''} cards-dropdown-popup ${columnClass} related-items-dropdown-popup`.trim(),
      },
    };
  }, [dropdownClassName, createItemAction, onCreateItemClick]);

  return (
    <NuvoBox display="flex" flexDirection="row" width="100%">
      <If condition={showButton}>
        <NuvoBox
          display="flex"
          alignItems="center"
          border={border}
          borderRight="0px"
          borderRadius={`${borderRadius} 0 0 ${borderRadius}`}>
          <NuvoTooltip enabled={tooltipEnabled} content={tooltipMsg}>
            <TMMButton icon={icon} onClick={onClick} disabled={buttonDisabled} />
          </NuvoTooltip>
        </NuvoBox>
      </If>
      <NuvoBox flex="1" style={{ overflow: 'visible' }}>
        <SelectBox
          key={dynamicKey}
          displayExpr={displayExpr}
          valueExpr={valueExpr}
          dataSource={selectDataSource}
          searchEnabled={searchEnabled}
          searchMode="contains"
          searchTimeout={500}
          minSearchLength={0}
          showClearButton={true}
          placeholder={selectProps.placeholder || 'Search and select...'}
          acceptCustomValue={false}
          dropDownOptions={dropdownOptions}
          itemRender={itemRender}
          {...selectProps}
          value={currentValue as any}
          onValueChanged={onValueChanged}>
          {isItemRequired && (
            <Validator validationGroup={validationGroup}>
              <RequiredRule key={dataField} message={`"${selectProps.label}" is required`} />
            </Validator>
          )}
        </SelectBox>
      </NuvoBox>
      {createItemAction && showCreateItemModal && (
        <RelatedItemsCreateModal visible modal={resolvedModal} onClose={closeCreateItemModal} />
      )}
    </NuvoBox>
  );
};

export default React.memo(RelatedItems);
